import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, MarkerClustererF, OverlayViewF, CircleF } from "@react-google-maps/api";
import { MapPin, Tag, Loader2, Navigation, Globe, X, Building2, Clock, LayoutList, Maximize2, Minimize2, Crosshair, Share2, Flame, DoorOpen, BadgeCheck } from "lucide-react";
import api from "../api";
import { useLocationFilter } from "../context/LocationContext";
import { useFx } from "../context/FxContext";
import { distanceKm, formatDistance } from "../utils/geo";
import { isLive, getRemaining, remainingLabel } from "../utils/countdown";
import { symbolFor } from "../utils/currency";
import { categoryMeta } from "../mock";
import { flagFor } from "../utils/flags";
import { useEnabledCategories } from "../hooks/useEnabledCategories";
import { useLang } from "../context/LanguageContext";
import { catLabel } from "../utils/categoryLabel";
import { dealOpenState } from "../utils/hours";
import InviteBusiness from "../components/InviteBusiness";
import NoDealsHere from "../components/NoDealsHere";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { BellPlus, Zap } from "lucide-react";
import { dealPinIcon } from "../utils/mapPin";
import { isBoosted, boostCounts, promotedIds, byProximityBoost, leadBoostId } from "../utils/boost";
import { thumb } from "../utils/image";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const containerStyle = { width: "100%", height: "520px" };
const SOON_MS = 24 * 3600 * 1000;
const endsSoon = (p) => !!p.expiresAt && new Date(p.expiresAt).getTime() - Date.now() < SOON_MS;
const catOf = (p) => categoryMeta(p.category)?.name || "Other";
const directionsUrl = (p) => `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;

// "open" | "closed" | "vacation" | "unknown" for a pin, using the business's structured hours.
const pinOpenState = (p) =>
  dealOpenState({ openingHours: p.openingHours, vacation: p.vacation, hours: p.hours }, { hours: p.branchHours, timezone: p.branchTimezone });

// Savings heat: colour bands for the deepest discount inside a map cell.
const HEAT_BANDS = [
  { min: 50, color: "#1d4ed8", opacity: 0.36 },
  { min: 35, color: "#0d9488", opacity: 0.34 },
  { min: 20, color: "#84cc16", opacity: 0.34 },
  { min: 0, color: "#fde047", opacity: 0.34 },
];
const heatBand = (pct) => HEAT_BANDS.find((b) => pct >= b.min) || HEAT_BANDS[HEAT_BANDS.length - 1];

const COUNTRY_CENTER = {
  "Trinidad & Tobago": { lat: 10.69, lng: -61.22, zoom: 9 },
  "Jamaica": { lat: 18.11, lng: -77.30, zoom: 8 },
  "Barbados": { lat: 13.19, lng: -59.54, zoom: 11 },
  "Guyana": { lat: 6.80, lng: -58.16, zoom: 8 },
  "Grenada": { lat: 12.12, lng: -61.68, zoom: 11 },
  "Saint Lucia": { lat: 13.90, lng: -60.98, zoom: 10 },
};

const REGION_OF = {
  // Caribbean
  "Trinidad & Tobago": "Caribbean", "Barbados": "Caribbean", "Jamaica": "Caribbean", "Grenada": "Caribbean",
  "Saint Lucia": "Caribbean", "Guyana": "Caribbean", "Suriname": "Caribbean", "Bahamas": "Caribbean",
  "Dominican Republic": "Caribbean", "Puerto Rico": "Caribbean", "Haiti": "Caribbean", "Cuba": "Caribbean",
  "Antigua & Barbuda": "Caribbean", "Saint Vincent & the Grenadines": "Caribbean", "Dominica": "Caribbean",
  "Saint Kitts & Nevis": "Caribbean", "Cayman Islands": "Caribbean", "Aruba": "Caribbean", "Curaçao": "Caribbean",
  // North / Central / South America
  "United States": "North America", "Canada": "North America", "Mexico": "North America",
  "Belize": "Central America", "Guatemala": "Central America", "Honduras": "Central America",
  "El Salvador": "Central America", "Nicaragua": "Central America", "Costa Rica": "Central America", "Panama": "Central America",
  "Brazil": "South America", "Argentina": "South America", "Colombia": "South America", "Chile": "South America",
  "Peru": "South America", "Ecuador": "South America", "Uruguay": "South America", "Bolivia": "South America",
  "Paraguay": "South America", "Venezuela": "South America",
  // Europe
  "United Kingdom": "Europe", "Ireland": "Europe", "France": "Europe", "Germany": "Europe", "Spain": "Europe",
  "Italy": "Europe", "Portugal": "Europe", "Netherlands": "Europe", "Belgium": "Europe", "Sweden": "Europe",
  "Norway": "Europe", "Denmark": "Europe", "Finland": "Europe", "Switzerland": "Europe", "Austria": "Europe",
  "Poland": "Europe", "Czechia": "Europe", "Greece": "Europe", "Turkey": "Europe", "Hungary": "Europe",
  "Romania": "Europe", "Croatia": "Europe", "Iceland": "Europe",
  // Middle East
  "United Arab Emirates": "Middle East", "Saudi Arabia": "Middle East", "Qatar": "Middle East",
  "Kuwait": "Middle East", "Bahrain": "Middle East", "Oman": "Middle East", "Israel": "Middle East",
  "Jordan": "Middle East", "Lebanon": "Middle East",
  // Africa
  "South Africa": "Africa", "Nigeria": "Africa", "Kenya": "Africa", "Egypt": "Africa", "Morocco": "Africa",
  "Ghana": "Africa", "Tanzania": "Africa", "Ethiopia": "Africa", "Uganda": "Africa", "Senegal": "Africa", "Tunisia": "Africa",
  // Asia
  "India": "Asia", "China": "Asia", "Japan": "Asia", "Singapore": "Asia", "Thailand": "Asia", "Vietnam": "Asia",
  "Indonesia": "Asia", "Philippines": "Asia", "Malaysia": "Asia", "South Korea": "Asia", "Hong Kong": "Asia",
  "Taiwan": "Asia", "Sri Lanka": "Asia", "Pakistan": "Asia", "Bangladesh": "Asia", "Nepal": "Asia", "Cambodia": "Asia",
  // Oceania
  "Australia": "Oceania", "New Zealand": "Oceania", "Fiji": "Oceania",
};
const REGION_ORDER = ["Caribbean", "North America", "Central America", "South America", "Europe", "Middle East", "Africa", "Asia", "Oceania", "Other"];
// Mainland frames for every country we have deals in — Google's own viewports include far-flung
// territories (Alaska + Hawaii for the US, French Guiana for France) and look broken on a map.
const COUNTRY_BOUNDS = {
  "United States": { sw: { lat: 24.5, lng: -125.0 }, ne: { lat: 49.5, lng: -66.5 } },
  "Canada": { sw: { lat: 42.0, lng: -140.0 }, ne: { lat: 70.0, lng: -52.0 } },
  "Mexico": { sw: { lat: 14.5, lng: -118.4 }, ne: { lat: 32.7, lng: -86.7 } },
  "Trinidad & Tobago": { sw: { lat: 10.0, lng: -61.95 }, ne: { lat: 11.4, lng: -60.5 } },
  "Barbados": { sw: { lat: 13.03, lng: -59.68 }, ne: { lat: 13.34, lng: -59.41 } },
  "Jamaica": { sw: { lat: 17.68, lng: -78.4 }, ne: { lat: 18.55, lng: -76.18 } },
  "Grenada": { sw: { lat: 11.98, lng: -61.82 }, ne: { lat: 12.33, lng: -61.57 } },
  "Saint Lucia": { sw: { lat: 13.7, lng: -61.09 }, ne: { lat: 14.12, lng: -60.86 } },
  "Guyana": { sw: { lat: 1.2, lng: -61.4 }, ne: { lat: 8.6, lng: -56.5 } },
  "Brazil": { sw: { lat: -33.8, lng: -74.0 }, ne: { lat: 5.3, lng: -34.8 } },
  "Argentina": { sw: { lat: -55.0, lng: -73.6 }, ne: { lat: -21.8, lng: -53.6 } },
  "Colombia": { sw: { lat: -4.2, lng: -79.0 }, ne: { lat: 12.5, lng: -66.9 } },
  "Chile": { sw: { lat: -56.0, lng: -76.0 }, ne: { lat: -17.5, lng: -66.0 } },
  "United Kingdom": { sw: { lat: 49.8, lng: -8.7 }, ne: { lat: 59.5, lng: 2.0 } },
  "Ireland": { sw: { lat: 51.4, lng: -10.5 }, ne: { lat: 55.4, lng: -5.4 } },
  "France": { sw: { lat: 41.3, lng: -5.2 }, ne: { lat: 51.2, lng: 9.6 } },
  "Germany": { sw: { lat: 47.3, lng: 5.9 }, ne: { lat: 55.1, lng: 15.0 } },
  "Spain": { sw: { lat: 35.9, lng: -9.6 }, ne: { lat: 43.9, lng: 4.4 } },
  "Portugal": { sw: { lat: 36.9, lng: -9.6 }, ne: { lat: 42.2, lng: -6.1 } },
  "Italy": { sw: { lat: 36.6, lng: 6.6 }, ne: { lat: 47.1, lng: 18.5 } },
  "Netherlands": { sw: { lat: 50.7, lng: 3.3 }, ne: { lat: 53.6, lng: 7.2 } },
  "Sweden": { sw: { lat: 55.3, lng: 11.0 }, ne: { lat: 69.1, lng: 24.2 } },
  "Switzerland": { sw: { lat: 45.8, lng: 5.9 }, ne: { lat: 47.8, lng: 10.5 } },
  "Greece": { sw: { lat: 34.8, lng: 19.4 }, ne: { lat: 41.8, lng: 28.2 } },
  "Turkey": { sw: { lat: 35.8, lng: 25.6 }, ne: { lat: 42.1, lng: 44.8 } },
  "United Arab Emirates": { sw: { lat: 22.6, lng: 51.5 }, ne: { lat: 26.1, lng: 56.4 } },
  "Egypt": { sw: { lat: 22.0, lng: 24.7 }, ne: { lat: 31.7, lng: 36.9 } },
  "Morocco": { sw: { lat: 27.6, lng: -13.2 }, ne: { lat: 35.9, lng: -1.0 } },
  "Nigeria": { sw: { lat: 4.2, lng: 2.7 }, ne: { lat: 13.9, lng: 14.7 } },
  "Kenya": { sw: { lat: -4.7, lng: 33.9 }, ne: { lat: 5.5, lng: 41.9 } },
  "South Africa": { sw: { lat: -35.0, lng: 16.4 }, ne: { lat: -22.1, lng: 32.9 } },
  "India": { sw: { lat: 6.7, lng: 68.1 }, ne: { lat: 35.7, lng: 97.4 } },
  "China": { sw: { lat: 18.0, lng: 73.5 }, ne: { lat: 53.6, lng: 135.1 } },
  "Japan": { sw: { lat: 30.9, lng: 129.0 }, ne: { lat: 45.6, lng: 146.0 } },
  "Singapore": { sw: { lat: 1.16, lng: 103.6 }, ne: { lat: 1.48, lng: 104.1 } },
  "Thailand": { sw: { lat: 5.6, lng: 97.3 }, ne: { lat: 20.5, lng: 105.6 } },
  "Vietnam": { sw: { lat: 8.2, lng: 102.1 }, ne: { lat: 23.4, lng: 109.5 } },
  "Indonesia": { sw: { lat: -11.0, lng: 95.0 }, ne: { lat: 6.1, lng: 141.0 } },
  "Philippines": { sw: { lat: 4.6, lng: 116.9 }, ne: { lat: 19.6, lng: 126.6 } },
  "Malaysia": { sw: { lat: 0.8, lng: 99.6 }, ne: { lat: 7.4, lng: 119.3 } },
  "Australia": { sw: { lat: -44.0, lng: 112.9 }, ne: { lat: -10.0, lng: 154.0 } },
  "New Zealand": { sw: { lat: -47.3, lng: 166.3 }, ne: { lat: -34.3, lng: 178.6 } },
};
const REGION_EMOJI = {
  "Caribbean": "🏝️", "North America": "🗽", "Central America": "🌋", "South America": "🦙", "Europe": "🏰",
  "Middle East": "🕌", "Africa": "🦁", "Asia": "🏯", "Oceania": "🦘", "Other": "🌐",
};
// Hand-tuned frames — geocoded continent viewports are far too wide (Alaska/Greenland dominate).
const REGION_BOUNDS = {
  "Caribbean": { sw: { lat: 9.5, lng: -85.5 }, ne: { lat: 27.0, lng: -58.5 } },
  "North America": { sw: { lat: 15.0, lng: -126.0 }, ne: { lat: 56.0, lng: -62.0 } },
  "Central America": { sw: { lat: 6.5, lng: -93.0 }, ne: { lat: 19.0, lng: -76.5 } },
  "South America": { sw: { lat: -55.0, lng: -81.0 }, ne: { lat: 13.0, lng: -34.0 } },
  "Europe": { sw: { lat: 35.0, lng: -11.0 }, ne: { lat: 64.0, lng: 32.0 } },
  "Middle East": { sw: { lat: 12.0, lng: 33.0 }, ne: { lat: 40.0, lng: 60.0 } },
  "Africa": { sw: { lat: -35.0, lng: -18.0 }, ne: { lat: 37.0, lng: 52.0 } },
  "Asia": { sw: { lat: -10.0, lng: 60.0 }, ne: { lat: 53.0, lng: 146.0 } },
  "Oceania": { sw: { lat: -47.0, lng: 110.0 }, ne: { lat: 0.0, lng: 180.0 } },
};
const regionOf = (c) => REGION_OF[c] || "Other";
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
// Remember the shopper's last map area for 30 days so LyteMap opens where they left off.
const VIEW_KEY = "lp-map-view";
const REGION_KEY = "lp-map-region";
const VIEW_TTL = 30 * 24 * 3600 * 1000;
const lastMapView = () => {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) || "null");
    if (!v || Date.now() - (v.at || 0) > VIEW_TTL || v.lat == null || v.zoom == null) return null;
    return { lat: v.lat, lng: v.lng, zoom: v.zoom };
  } catch { return null; }
};
const saveMapView = (map) => {
  const c = map?.getCenter?.();
  const z = map?.getZoom?.();
  if (!c || !z) return;
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify({ lat: +c.lat().toFixed(5), lng: +c.lng().toFixed(5), zoom: z, at: Date.now() }));
  } catch { /* storage full / private mode */ }
};
// Median-trim a coordinate list so one far-away branch can't stretch a country/region view.
const coreCoords = (list, km = 900) => {
  if (list.length < 3) return list;
  const med = (arr) => [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)];
  const mlat = med(list.map((p) => p.lat)); const mlng = med(list.map((p) => p.lng));
  const core = list.filter((p) => distanceKm(mlat, mlng, p.lat, p.lng) <= km);
  return core.length ? core : list;
};

const LyteMap = () => {
  const enabledCats = useEnabledCategories();
  const { isLoaded } = useJsApiLoader({ id: "gmap-script", googleMapsApiKey: GKEY || "" });
  const { country, gps, place, unit, locating, geoError, requestLocation, setGps, setPlace, setCountry, autoCountry, clearAutoCountry } = useLocationFilter();
  const { approx } = useFx();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mapWide, setMapWide] = useState(false);
  const { toast } = useToast();
  const [pins, setPins] = useState([]);
  const [places, setPlaces] = useState([]);
  const [region, setRegion] = useState(() => localStorage.getItem(REGION_KEY) || null);
  const [active, setActive] = useState(null);
  const [promptDismissed, setPromptDismissed] = useState(() => sessionStorage.getItem("lp-geo-prompt-dismissed") === "1");
  const [catFilter, setCatFilter] = useState("All");
  const [soonOnly, setSoonOnly] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);
  // Savings heat is retired — the colour wash competed with the pins and told shoppers
  // nothing the discount badges already say.
  const heatOn = false;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [inViewOnly, setInViewOnly] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNote, setShareNote] = useState("");
  const [watching, setWatching] = useState(false);
  const mapRef = useRef(null);
  const mapBoxRef = useRef(null);

  // ---- Shareable map view + "pick up where you left off" ----
  const [sp] = useSearchParams();
  const sharedView = useRef(
    sp.get("lat") && sp.get("lng")
      ? { lat: +sp.get("lat"), lng: +sp.get("lng"), zoom: +(sp.get("z") || 13) }
      : lastMapView()
  ).current;
  const pendingShared = useRef(!!sharedView);
  const pendingFit = useRef(null);
  useEffect(() => {
    if (sp.get("cat")) setCatFilter(sp.get("cat"));
    if (sp.get("soon") === "1") setSoonOnly(true);
    if (sp.get("open") === "1") setOpenOnly(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // A real location change (top-bar city/country, Near Me) releases the remembered/shared view.
  const locKey = `${country}|${gps ? `${gps.lat},${gps.lng}` : ""}`;
  const locRef = useRef(locKey);
  useEffect(() => {
    if (locKey !== locRef.current) { locRef.current = locKey; pendingShared.current = false; }
  }, [locKey]);

  const applySharedView = useCallback(() => {
    const m = mapRef.current;
    if (!m || !sharedView) return false;
    m.setCenter({ lat: sharedView.lat, lng: sharedView.lng });
    m.setZoom(sharedView.zoom);
    return true;
  }, [sharedView]);


  const dismissPrompt = () => { setPromptDismissed(true); sessionStorage.setItem("lp-geo-prompt-dismissed", "1"); };
  const useMyLocation = async () => { pendingShared.current = false; await requestLocation(); };

  // One place to park "where the map should go next" — consumed once the pins for that pick land.
  const applyPendingFit = useCallback(() => {
    const m = mapRef.current;
    const pf = pendingFit.current;
    if (!m || !window.google || !pf) return;
    // Keep the pick for a moment so the refetched pins can't snap the view back.
    if (Date.now() - pf.at > 2500) { pendingFit.current = null; return; }
    if (pf.bounds) { m.fitBounds(pf.bounds, 24); return; }
    if (pf.center) { m.setCenter(pf.center); m.setZoom(pf.zoom || 13); return; }
    const pts = pf.coords || [];
    if (!pts.length) { m.setCenter({ lat: 20, lng: 0 }); m.setZoom(2); return; }
    if (pts.length === 1) { m.setCenter(pts[0]); m.setZoom(12); return; }
    const b = new window.google.maps.LatLngBounds();
    pts.forEach((p) => b.extend(p));
    m.fitBounds(b, 40);
  }, []);
  const queueFit = (pf) => { pendingFit.current = { ...pf, at: Date.now() }; setTimeout(applyPendingFit, 80); };

  // "Show me the whole area": Google knows the real viewport of a region/country name.
  const areaCache = useRef({});
  const fitArea = (name) => {
    if (!window.google) return;
    const cached = areaCache.current[name];
    if (cached) { queueFit({ bounds: cached }); return; }
    new window.google.maps.Geocoder().geocode({ address: name }, (res, status) => {
      const vp = status === "OK" && res?.[0]?.geometry?.viewport;
      if (!vp) return;
      // Ignore absurdly wide viewports (overseas territories) — the pin-derived fit reads better.
      const ne = vp.getNorthEast(); const sw = vp.getSouthWest();
      let lngSpan = ne.lng() - sw.lng();
      if (lngSpan < 0) lngSpan += 360;
      if (ne.lat() - sw.lat() > 45 || lngSpan > 60) return;
      areaCache.current[name] = vp;
      queueFit({ bounds: vp });
    });
  };

  const allCityCoords = (list) => coreCoords(list.flatMap((c) => c.cities.map((x) => ({ lat: x.lat, lng: x.lng }))));

  const pickWorld = () => {
    pendingShared.current = false;
    dismissPrompt(); setActive(null); setRegion(null);
    setGps(null); setPlace(""); setCountry("");
    queueFit({ coords: places.flatMap((c) => c.cities.map((x) => ({ lat: x.lat, lng: x.lng }))) });
  };
  const pickRegion = (r) => {
    pendingShared.current = false;
    dismissPrompt(); setActive(null); setRegion(r.region);
    setGps(null); setPlace(""); setCountry("");
    queueFit({ coords: allCityCoords(r.countries) });
    const rb = REGION_BOUNDS[r.region];
    if (rb && window.google) queueFit({ bounds: new window.google.maps.LatLngBounds(rb.sw, rb.ne) });
    else fitArea(r.region === "Other" ? "world" : r.region);
  };
  const pickCountry = (c) => {
    pendingShared.current = false;
    dismissPrompt(); setActive(null); setRegion(regionOf(c.country));
    setGps(null); setPlace(""); setCountry(c.country);
    queueFit({ coords: coreCoords(c.cities.map((x) => ({ lat: x.lat, lng: x.lng })), 600) });
    const cb = COUNTRY_BOUNDS[c.country];
    if (cb && window.google) queueFit({ bounds: new window.google.maps.LatLngBounds(cb.sw, cb.ne) });
    else fitArea(c.country);
  };
  const pickCity = (c) => {
    pendingShared.current = false;
    dismissPrompt(); setActive(null);
    setGps({ lat: c.lat, lng: c.lng }); setPlace(c.city);
    queueFit({ center: { lat: c.lat, lng: c.lng }, zoom: 13 });
  };
  const showPrompt = !gps && !promptDismissed;
  const [verifiedOnly, setVerifiedOnly] = useState(() => localStorage.getItem("lp-verified-only") === "1");

  useEffect(() => {
    setActive(null);
    const params = gps ? {} : { country };
    if (verifiedOnly) params.verifiedOnly = true;
    api.get("/deals/pins", { params }).then((r) => {
      // With a location, the map is about "deals around here" — drop far-away pins so counts match what's on screen
      const live = r.data.filter(isLive);
      setPins(gps ? live.filter((p) => p.lat != null && distanceKm(gps.lat, gps.lng, p.lat, p.lng) <= 150) : live);
    }).catch(() => setPins([]));
  }, [country, gps, verifiedOnly]);

  useEffect(() => {
    if (region) localStorage.setItem(REGION_KEY, region); else localStorage.removeItem(REGION_KEY);
  }, [region]);

  useEffect(() => {
    api.get("/deals/places").then((r) => setPlaces(Array.isArray(r.data) ? r.data : [])).catch(() => setPlaces([]));
  }, []);

  const fit = useCallback((list) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    let pts = list.filter((p) => p.lat != null);
    if (gps) {
      const near = pts.filter((p) => distanceKm(gps.lat, gps.lng, p.lat, p.lng) <= 150);
      if (near.length === 0) { map.setCenter({ lat: gps.lat, lng: gps.lng }); map.setZoom(12); return; }
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: gps.lat, lng: gps.lng });
      near.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, 70);
      return;
    }
    if (pts.length === 0) {
      const cb = country && COUNTRY_BOUNDS[country];
      if (cb && window.google) {
        map.fitBounds(new window.google.maps.LatLngBounds(cb.sw, cb.ne), 40);
        return;
      }
      const fb = COUNTRY_CENTER[country] || { lat: 10.69, lng: -61.22, zoom: 9 };
      map.setCenter({ lat: fb.lat, lng: fb.lng }); map.setZoom(fb.zoom); return;
    }
    if (country && pts.length > 2) {
      // A country's deals can include branches abroad (multinationals) — don't let them stretch the view.
      const med = (arr) => [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)];
      const mlat = med(pts.map((p) => p.lat)); const mlng = med(pts.map((p) => p.lng));
      const core = pts.filter((p) => distanceKm(mlat, mlng, p.lat, p.lng) <= 600);
      if (core.length) pts = core;
    }
    if (pts.length === 1) { map.setCenter({ lat: pts[0].lat, lng: pts[0].lng }); map.setZoom(13); return; }
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 70);
  }, [country, gps]);

  useEffect(() => {
    // A shared link owns the view until the shopper explicitly moves the map (quick pick, my location, fit, expand).
    if (pendingShared.current) { applySharedView(); return; }
    if (pendingFit.current && Date.now() - pendingFit.current.at < 2500) { applyPendingFit(); return; }
    fit(pins);
  }, [pins, fit, applySharedView, applyPendingFit]);

  // Category chips only for categories that actually have pins in this area
  const catCounts = {};
  pins.forEach((p) => { const c = catOf(p); catCounts[c] = (catCounts[c] || 0) + 1; });
  const presentCats = enabledCats.filter((c) => catCounts[c.name] && !["Near Expiry", "Upcoming"].includes(c.name));
  const soonCount = pins.filter(endsSoon).length;
  const openCount = pins.filter((p) => pinOpenState(p) === "open").length;
  const visiblePins = pins.filter((p) => (catFilter === "All" || catOf(p) === catFilter) && (!soonOnly || endsSoon(p)) && (!openOnly || pinOpenState(p) === "open"));
  useEffect(() => { if (catFilter !== "All" && !catCounts[catFilter]) setCatFilter("All"); }, [pins]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group pins back into deals for the sidebar
  const dealsMap = {};
  visiblePins.forEach((p) => {
    if (!dealsMap[p.dealId]) dealsMap[p.dealId] = { ...p, branches: [] };
    dealsMap[p.dealId].branches.push(p);
  });
  let dealList = Object.values(dealsMap);
  const dealKm = (d) => (gps ? Math.min(...d.branches.map((b) => distanceKm(gps.lat, gps.lng, b.lat, b.lng))) : null);
  // A chain can have several pins on one deal — the card and its Directions link must point at the
  // branch nearest the shopper, not whichever one happened to come back first.
  const nearestBranch = (d) => {
    const list = d.branches || [];
    if (!gps || list.length < 2) return list[0] || d;
    return list.reduce((best, b) =>
      distanceKm(gps.lat, gps.lng, b.lat, b.lng) < distanceKm(gps.lat, gps.lng, best.lat, best.lng) ? b : best);
  };
  dealList = dealList.map((d) => ({ ...d, _d: dealKm(d) }));
  const totalDeals = dealList.length;
  // Paid placement never buries a closer shop: deals are banded by distance from the shopper
  // first, and a boost only wins the top spot inside its own band (and within 10km). A list also
  // never shows more than 3 sponsored deals (or ~30% of a short list) — the rest fall back to
  // their normal position. Sponsored deals rotate hourly so every paying shop gets turns.
  const promoted = promotedIds(dealList, (d) => d.dealId, (d) => d._d);
  dealList = [...dealList].sort(byProximityBoost((d) => d.dealId, (d) => d._d, promoted));
  const pinKm = (p) => (gps ? distanceKm(gps.lat, gps.lng, p.lat, p.lng) : null);
  const leadBoost = leadBoostId(visiblePins, (p) => p.dealId, pinKm, promoted);
  if (inViewOnly && bounds) dealList = dealList.filter((d) => d.branches.some((b) => bounds.contains({ lat: b.lat, lng: b.lng })));

  // On phones the deal list sits under the map, so tapping a card must bring the map back into view.
  const revealMap = () => {
    if (window.innerWidth < 1024) mapBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const focusPin = (p) => { setActive(p); revealMap(); if (mapRef.current) { mapRef.current.panTo({ lat: p.lat, lng: p.lng }); mapRef.current.setZoom(14); } };
  const selectPin = (p) => { setActive(p); if (mapRef.current) mapRef.current.panTo({ lat: p.lat, lng: p.lng }); };
  const resetView = () => { pendingShared.current = false; setActive(null); fit(visiblePins.length ? visiblePins : pins); };

  // The five closest deals as one link — built for a WhatsApp group, which is how things
  // actually travel here.
  const shareNear = async () => {
    const m = mapRef.current;
    const mc = m?.getCenter();
    const c = gps || (mc ? { lat: mc.lat(), lng: mc.lng() } : null);
    if (!c) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/near?lat=${c.lat.toFixed(5)}&lng=${c.lng.toFixed(5)}${place ? `&place=${encodeURIComponent(place)}` : ""}`;
    const text = t("map.shareNearMsg", { place: place || country || "" });
    try {
      if (navigator.share) await navigator.share({ title: "LytePress", text, url });
      else window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
    } catch { /* user dismissed the share sheet */ }
  };
  // Expanding the map changes its width, so re-fit once the layout has settled.
  const toggleWide = () => {
    pendingShared.current = false;
    setMapWide((v) => !v);
    setTimeout(() => fit(visiblePins.length ? visiblePins : pins), 320);
  };

  // Several deals can sit at the same address: one business with 5 offers, OR a plaza/mall where
  // different businesses geocode a few metres apart. Stacked markers hide each other, so group any
  // pins within ~45m into ONE counted pin (proximity, not a grid — grid cells split neighbours).
  const SPOT_M = 45;
  const spotKey = (p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
  const spots = useMemo(() => {
    const groups = [];
    visiblePins.forEach((p) => {
      if (p.lat == null) return;
      const g = groups.find((grp) => distanceKm(grp[0].lat, grp[0].lng, p.lat, p.lng) * 1000 <= SPOT_M);
      if (g) g.push(p); else groups.push([p]);
    });
    return groups;
  }, [visiblePins]); // eslint-disable-line react-hooks/exhaustive-deps
  const samePin = (a, b) => a.dealId === b.dealId && a.branchIndex === b.branchIndex;
  const activeGroup = active && active.lat != null
    ? (spots.find((g) => g.some((x) => samePin(x, active))) || [active])
    : [];
  // Inside one spot, group the deals under their business so a plaza reads clearly.
  const bizMap = {};
  activeGroup.forEach((p) => { (bizMap[p.business] = bizMap[p.business] || []).push(p); });
  const activeBusinesses = Object.entries(bizMap);

  // Shared addresses (malls/plazas) get a venue name so shoppers recognise the place at a glance.
  const [venues, setVenues] = useState({});
  useEffect(() => {
    if (!active || active.lat == null || activeBusinesses.length < 2) return;
    const k = spotKey(activeGroup[0]);
    if (venues[k] !== undefined) return;
    setVenues((v) => ({ ...v, [k]: "" }));
    api.get(`/venue?lat=${activeGroup[0].lat}&lng=${activeGroup[0].lng}`)
      .then((r) => setVenues((v) => ({ ...v, [k]: r.data?.name || "" })))
      .catch(() => {});
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  const spotLabel = activeBusinesses.length > 1
    ? (venues[spotKey(activeGroup[0])]
        ? `${venues[spotKey(activeGroup[0])]} · ${t("map.shopsHere", { n: activeBusinesses.length })}`
        : t("map.sharedAddress", { n: activeBusinesses.length }))
    : "";

  // Quick picks: region → country → city.
  const regionGroups = useMemo(() => {
    const m = {};
    places.forEach((c) => {
      const r = regionOf(c.country);
      const e = m[r] || (m[r] = { region: r, deals: 0, countries: [] });
      e.deals += c.deals;
      e.countries.push(c);
    });
    // Every region stays on the row (even at 0 deals) so shoppers can see the whole world's coverage.
    return REGION_ORDER.filter((r) => m[r] || r !== "Other").map((r) => {
      const e = m[r] || { region: r, deals: 0, countries: [] };
      e.countries.sort((a, b) => b.deals - a.deals || a.country.localeCompare(b.country));
      return e;
    });
  }, [places]);
  const activeRegionName = region || (country ? regionOf(country) : null);
  const activeRegion = regionGroups.find((r) => r.region === activeRegionName) || null;
  const activeCountry = places.find((p) => p.country === country) || null;

  // Savings heat: bucket the visible pins into zoom-sized cells and wash each cell in its best discount.
  const heatCells = useMemo(() => {
    if (!heatOn || !zoom) return [];
    const cell = Math.max(0.0015, 100 / Math.pow(2, zoom));
    const m = {};
    visiblePins.forEach((p) => {
      if (p.lat == null) return;
      const gy = Math.floor(p.lat / cell); const gx = Math.floor(p.lng / cell);
      const k = `${gy}:${gx}`;
      const e = m[k] || (m[k] = { key: k, lat: (gy + 0.5) * cell, lng: (gx + 0.5) * cell, best: 0, n: 0 });
      e.best = Math.max(e.best, p.discount || 0);
      e.n += 1;
    });
    const radius = cell * 111320 * 0.45;
    return Object.values(m).map((c) => ({ ...c, radius }));
  }, [heatOn, zoom, visiblePins]); // eslint-disable-line react-hooks/exhaustive-deps

  const shareView = async () => {
    const m = mapRef.current;
    const c = m?.getCenter();
    const q = new URLSearchParams();
    if (c) { q.set("lat", c.lat().toFixed(5)); q.set("lng", c.lng().toFixed(5)); q.set("z", String(m.getZoom() || 13)); }
    if (catFilter !== "All") q.set("cat", catFilter);
    if (soonOnly) q.set("soon", "1");
    if (openOnly) q.set("open_now", "1");
    if (verifiedOnly) q.set("verified", "1");
    if (shareNote.trim()) q.set("note", shareNote.trim().slice(0, 140));
    // The /api/share/map wrapper unfurls as "12 deals in Woodbrook — save up to 60%" on WhatsApp.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/map?${q.toString()}`;
    const title = t("map.shareTitle");
    setShareOpen(false);
    try {
      if (navigator.share) { await navigator.share({ title, text: shareNote.trim() || title, url }); return; }
      await navigator.clipboard.writeText(url);
      toast({ title: t("map.linkCopied"), description: url });
    } catch (e) {
      toast({ title: t("map.shareView"), description: url });
    }
  };

  // Radius (km) that roughly covers what's on screen, for "alert me about this area".
  const viewRadiusKm = () => {
    const m = mapRef.current;
    const b = m?.getBounds?.();
    const c = m?.getCenter?.();
    if (!b || !c) return 5;
    const ne = b.getNorthEast();
    return Math.max(0.5, Math.min(50, distanceKm(c.lat(), c.lng(), ne.lat(), ne.lng())));
  };

  const watchThisArea = async () => {
    if (!user) { toast({ title: t("map.alertSignIn") }); navigate("/login"); return; }
    const m = mapRef.current;
    const c = m?.getCenter?.();
    if (!c) return;
    const lat = c.lat(); const lng = c.lng();
    setWatching(true);
    try {
      let label = place && place !== "your location" ? place : "";
      if (!label) {
        try {
          const { data } = await api.get("/reverse-geocode", { params: { lat, lng } });
          label = data.city || (data.formatted ? data.formatted.split(",")[0] : "");
        } catch { /* fall back below */ }
      }
      const { data } = await api.post("/my/areas", {
        label: label || t("map.thisArea"), lat, lng,
        radiusKm: viewRadiusKm(), category: catFilter !== "All" ? catFilter : null,
      });
      toast({
        title: t("map.alertSaved", { area: data.label }),
        description: t("map.alertSavedDesc", { n: data.dealCount, km: Math.round(data.radiusKm) }),
      });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: typeof d === "string" ? d : t("map.alertFailed") });
    } finally { setWatching(false); }
  };

  const countDirections = (dealId) => { api.post(`/deals/${dealId}/directions`, null, { params: { src: "map" } }).catch(() => {}); };

  return (
    <div className="space-y-6 lp-fade-up">
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md" data-testid="map-share-dialog">
          <DialogHeader><DialogTitle>{t("map.shareDialogTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-secondary/50 p-3">
              <p className="text-sm font-bold text-foreground" data-testid="map-share-preview">
                {t("map.sharePreview", { n: visiblePins.length, best: Math.max(0, ...visiblePins.map((p) => p.discount || 0)) })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("map.sharePreviewNote")}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t("map.shareNoteLabel")}</label>
              <input value={shareNote} onChange={(e) => setShareNote(e.target.value.slice(0, 140))} data-testid="map-share-note"
                placeholder={t("map.shareNotePlaceholder")}
                className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-[#16a34a]" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShareOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-foreground px-4 py-2.5 rounded-xl">{t("map.shareCancel")}</button>
            <button onClick={shareView} data-testid="map-share-confirm"
              className="inline-flex items-center justify-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-colors">
              <Share2 size={15} /> {t("map.shareNow")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2">
            LyteMap
            {country && <span className="text-xl leading-none" title={country} data-testid="map-country-flag">{flagFor(country)}</span>}
          </h1>
          <p className="text-sm text-muted-foreground">{gps ? t("map.subtitleGps", { place: place && place !== "your location" ? place : t("map.yourLocation") }) : t("map.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShareOpen(true)} data-testid="map-share-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground bg-card border border-border hover:border-[#16a34a] hover:text-[#16a34a] px-4 py-2 rounded-xl transition-colors">
            <Share2 size={15} /> <span className="hidden sm:inline">{t("map.shareView")}</span>
          </button>
          <button onClick={watchThisArea} disabled={watching} data-testid="map-area-alert-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground bg-card border border-border hover:border-[#16a34a] hover:text-[#16a34a] disabled:opacity-60 px-4 py-2 rounded-xl transition-colors">
            {watching ? <Loader2 size={15} className="animate-spin" /> : <BellPlus size={15} />} <span className="hidden sm:inline">{t("map.alertMe")}</span>
          </button>
          {!gps && (
            <button onClick={useMyLocation} disabled={locating} className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] hover:bg-[#d6efdd] disabled:opacity-70 px-4 py-2 rounded-xl transition-colors" data-testid="use-my-location-btn">
              {locating ? <><Loader2 size={15} className="animate-spin" /> {t("home.locating")}</> : <><Navigation size={15} /> {t("topbar.useMyLocation")}</>}
            </button>
          )}
        </div>
      </div>

      {autoCountry && autoCountry === country && !gps && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] px-3.5 py-2.5" data-testid="geo-confirm-chip">
          <p className="text-sm font-semibold text-[#0e6b3e] dark:text-[#8fe0ac] flex items-center gap-2 min-w-0">
            <MapPin size={15} className="shrink-0" />
            <span className="truncate">{t("map.autoDetected", { country })}</span>
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => { clearAutoCountry(); pickWorld(); }} data-testid="geo-confirm-change"
              className="text-xs font-bold text-white bg-[#16a34a] hover:bg-[#15803d] rounded-lg px-3 py-1.5 transition-colors">
              {t("map.change")}
            </button>
            <button onClick={clearAutoCountry} data-testid="geo-confirm-dismiss" aria-label="Dismiss"
              className="text-[#0e6b3e] dark:text-[#8fe0ac] hover:opacity-70 rounded-lg p-1 transition-opacity">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2 overflow-x-auto lp-scroll pb-1 md:flex-wrap md:overflow-visible" data-testid="region-quick-picks">
          <span className="text-xs font-semibold text-muted-foreground shrink-0 flex items-center gap-1"><Globe size={13} className="text-[#16a34a]" /> {t("map.jumpTo")}</span>
          <button onClick={pickWorld} data-testid="quick-pick-world"
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${!activeRegionName && !gps ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-border text-foreground hover:border-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
            <span className="text-base leading-none">🌍</span> {t("map.world")}
          </button>
          {regionGroups.map((r) => {
            const on = activeRegionName === r.region;
            return (
              <button key={r.region} onClick={() => pickRegion(r)} data-testid={`region-pick-${slug(r.region)}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#16a34a] text-white border-[#16a34a]" : r.deals === 0 ? "bg-card border-dashed border-border text-muted-foreground hover:border-[#16a34a] hover:text-foreground" : "bg-card border-border text-foreground hover:border-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
                <span className="text-base leading-none">{REGION_EMOJI[r.region]}</span> {t(`reg.${r.region}`)} <span className="opacity-70">({r.deals})</span>
              </button>
            );
          })}
          <InviteBusiness city={place || ""} country={country || ""} />
        </div>

        {activeRegion && (
          <div className="flex items-center gap-2 overflow-x-auto lp-scroll pb-1 md:flex-wrap md:overflow-visible" data-testid="country-quick-picks">
            <span className="text-xs font-semibold text-muted-foreground shrink-0 flex items-center gap-1"><Globe size={13} className="text-[#16a34a]" /> {t("map.countriesIn", { region: t(`reg.${activeRegion.region}`) })}</span>
            <button onClick={() => pickRegion(activeRegion)} data-testid="country-pick-all"
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${!country && !gps ? "bg-[#0f2a1a] text-white border-[#0f2a1a]" : "bg-card border-border text-foreground hover:border-[#16a34a]"}`}>
              {t("map.allOf", { country: t(`reg.${activeRegion.region}`) })}
            </button>

            {activeRegion.countries.map((c) => {
              const on = country === c.country;
              return (
                <button key={c.country} onClick={() => pickCountry(c)} data-testid={`quick-pick-${slug(c.country)}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-border text-foreground hover:border-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
                  <span className="text-base leading-none">{flagFor(c.country)}</span> {c.country} <span className="opacity-70">({c.deals})</span>
                </button>
              );
            })}
          </div>
        )}

        {activeRegion && activeRegion.countries.length === 0 && (
          <p className="text-xs text-muted-foreground" data-testid="region-empty-note">{t("map.noDealsRegion")}</p>
        )}

        {activeCountry && activeCountry.cities.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto lp-scroll pb-1 md:flex-wrap md:overflow-visible" data-testid="city-quick-picks">
            <span className="text-xs font-semibold text-muted-foreground shrink-0 flex items-center gap-1"><MapPin size={13} className="text-[#16a34a]" /> {t("map.citiesIn", { country: activeCountry.country })}</span>
            <button onClick={() => pickCountry(activeCountry)} data-testid="city-pick-all"
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${!gps ? "bg-[#0f2a1a] text-white border-[#0f2a1a]" : "bg-card border-border text-foreground hover:border-[#16a34a]"}`}>
              {t("map.allOf", { country: activeCountry.country })}
            </button>
            {activeCountry.cities.map((c) => {
              const on = place === c.city;
              return (
                <button key={c.city} onClick={() => pickCity(c)} data-testid={`city-pick-${slug(c.city)}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-border text-foreground hover:border-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
                  {c.city} <span className="opacity-70">({c.deals})</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 overflow-x-auto lp-scroll pb-2 md:flex-wrap md:overflow-visible" data-testid="map-category-filters">
        <button onClick={() => setCatFilter("All")} data-testid="map-filter-all"
          className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${catFilter === "All" ? "bg-[#0f2a1a] text-white border-[#0f2a1a]" : "bg-card border-border text-foreground hover:border-[#16a34a]"}`}>
          {t("map.all")} <span className="opacity-70">({pins.length})</span>
        </button>
        <button onClick={() => setSoonOnly((v) => !v)} data-testid="map-filter-ending-soon" disabled={soonCount === 0}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors disabled:opacity-40 ${soonOnly ? "bg-[#dc2626] text-white border-[#dc2626]" : "bg-card border-[#dc2626]/40 text-[#dc2626] hover:bg-[#fde8e8] dark:hover:bg-[#3b1212]"}`}>
          <Clock size={13} /> {t("map.endingSoon")} <span className="opacity-70">({soonCount})</span>
        </button>
        <button onClick={() => { const v = !verifiedOnly; setVerifiedOnly(v); localStorage.setItem("lp-verified-only", v ? "1" : "0"); }}
          data-testid="map-filter-verified"
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${verifiedOnly ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-[#16a34a]/40 text-[#0e7a3c] dark:text-[#7ee0a3] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
          <BadgeCheck size={13} /> {t("map.verifiedOnly")}
        </button>
        <button onClick={() => setOpenOnly((v) => !v)} data-testid="map-filter-open-now" disabled={openCount === 0}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors disabled:opacity-40 ${openOnly ? "bg-[#0e7a3c] text-white border-[#0e7a3c]" : "bg-card border-[#16a34a]/40 text-[#0e7a3c] dark:text-[#7ee0a3] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
          <DoorOpen size={13} /> {t("map.openNow")} <span className="opacity-70">({openCount})</span>
        </button>
        {presentCats.map((c) => {
          const Icon = c.icon; const on = catFilter === c.name;
          return (
            <button key={c.name} onClick={() => setCatFilter(on ? "All" : c.name)} data-testid={`map-filter-${c.slug}`}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors"
              style={on ? { background: c.color, color: "#fff", borderColor: c.color } : { background: c.bg, color: c.color, borderColor: "transparent" }}>
              <Icon size={13} /> {catLabel(c.name, t)} <span className="opacity-70">({catCounts[c.name]})</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div ref={mapBoxRef} className={`relative rounded-2xl overflow-hidden border border-border shadow-sm ${mapWide ? "lg:col-span-3" : "lg:col-span-2"}`}>
          {showPrompt && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-sm p-6" data-testid="geo-prompt-overlay">
              <div className="relative bg-card border border-border rounded-2xl shadow-xl p-7 max-w-sm text-center">
                <button onClick={dismissPrompt} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" aria-label="Dismiss" data-testid="geo-prompt-dismiss"><X size={18} /></button>
                <span className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Navigation size={26} className="text-[#16a34a]" /></span>
                <h3 className="text-lg font-extrabold text-foreground">{t("map.geoTitle")}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{t("map.geoDesc")}</p>
                {geoError && (
                  <p className="text-xs text-[#e11d48] mt-3" data-testid="geo-prompt-error">
                    {geoError === "denied" ? "Location access was blocked. You can enable it in your browser, or search a city / pick a country in the top bar." : geoError === "unsupported" ? "Your browser doesn't support location. Try searching a city in the top bar." : "Couldn't get your location. Try again or search a city in the top bar."}
                  </p>
                )}
                <button onClick={useMyLocation} disabled={locating} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-70 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors" data-testid="geo-prompt-use-location">
                  {locating ? <><Loader2 size={16} className="animate-spin" /> {t("home.locating")}</> : <><Navigation size={16} /> {t("topbar.useMyLocation")}</>}
                </button>
                <button onClick={dismissPrompt} className="mt-2 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-2 rounded-xl" data-testid="geo-prompt-browse-country">
                  <Globe size={15} /> {t("map.maybeLaterBrowse", { place: country || t("map.allDeals") })}
                </button>
              </div>
            </div>
          )}
          {!GKEY ? (
            <div className="h-[520px] flex items-center justify-center bg-secondary text-muted-foreground text-sm">{t("lm.googleMapsApiKey")}</div>
          ) : !isLoaded ? (
            <div className="h-[520px] flex items-center justify-center bg-secondary text-muted-foreground"><Loader2 className="animate-spin mr-2" /> {t("lm.loadingMap")}</div>
          ) : (
            <GoogleMap mapContainerStyle={{ width: "100%", height: mapWide ? "640px" : "520px" }} onLoad={(m) => { mapRef.current = m; if (sharedView) applySharedView(); else fit(pins); }} onIdle={() => { const m = mapRef.current; const b = m?.getBounds(); if (b) setBounds(b); if (m) { setZoom(m.getZoom() || 0); saveMapView(m); } }} options={{ streetViewControl: false, mapTypeControl: false, clickableIcons: false, styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }] }}>
              {heatOn && heatCells.map((c) => {
                const band = heatBand(c.best);
                return (
                  <CircleF key={`heat-${c.key}`} center={{ lat: c.lat, lng: c.lng }} radius={c.radius}
                    options={{ strokeOpacity: 0, fillColor: band.color, fillOpacity: band.opacity, clickable: false, zIndex: 1 }} />
                );
              })}
              <MarkerClustererF>
                {(clusterer) => (
                  <>
                    {spots.map((group) => {
                      const p = group[0];
                      const soon = group.some(endsSoon);
                      const many = group.length > 1;
                      const best = Math.max(...group.map((g) => g.discount || 0));
                      const boosted = group.some((g) => boostCounts(g, pinKm(g)) && promoted.has(g.dealId));
                      const lead = !!leadBoost && group.some((g) => g.dealId === leadBoost.dealId);
                      return (
                        <MarkerF key={spotKey(p)} position={{ lat: p.lat, lng: p.lng }} clusterer={clusterer} optimized={false}
                          onClick={() => selectPin(p)} zIndex={lead ? 20 : boosted ? 14 : soon ? 10 : many ? 5 : 1}
                          title={many ? t("map.dealsHere", { n: group.length }) : `${p.title} — ${best}%`}
                          icon={dealPinIcon({ discount: best, soon, count: group.length, boosted, lead })} />
                      );
                    })}
                  </>
                )}
              </MarkerClustererF>
              {zoom >= 12 && spots.filter((g) => g.some(endsSoon)).map((g) => (
                <OverlayViewF key={`pulse-${spotKey(g[0])}`} position={{ lat: g[0].lat, lng: g[0].lng }} mapPaneName="overlayLayer" getPixelPositionOffset={() => ({ x: -11, y: -11 })}>
                  <div className="lp-pulse-ring" data-testid="ending-soon-pulse" />
                </OverlayViewF>
              ))}
              {gps && window.google && (
                <MarkerF position={{ lat: gps.lat, lng: gps.lng }} title={t("map.myLocation")} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#2563eb", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 }} />
              )}
              {active && (
                <InfoWindowF position={{ lat: active.lat, lng: active.lng }} onCloseClick={() => setActive(null)} options={{ maxWidth: 320, pixelOffset: new window.google.maps.Size(0, -6) }}>
                  <div className="lp-iw" data-testid="map-infowindow"
                    style={{ width: "min(302px, 80vw)", maxHeight: "min(46vh, 340px)", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", fontFamily: "Plus Jakarta Sans, ui-sans-serif, system-ui, sans-serif", overflowWrap: "anywhere", background: "#fff" }}>
                    {active.image ? (
                      <div style={{ position: "relative", height: 124, background: "#0f2a1a" }}>
                        <img src={thumb(active.image, 480)} alt={active.title} decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(8,20,13,0.42) 0%, rgba(8,20,13,0) 42%, rgba(8,20,13,0.80) 100%)" }} />
                        {active.discount > 0 && (
                          <span style={{ position: "absolute", top: 10, left: 12, background: "linear-gradient(180deg,#22c55e,#0e7a3c)", color: "#fff", fontSize: 14, fontWeight: 800, padding: "5px 11px", borderRadius: 999, letterSpacing: -0.2, boxShadow: "0 4px 14px rgba(8,20,13,0.35)" }}>{t("common.percentOff", { n: active.discount })}</span>
                        )}
                        {endsSoon(active) && (
                          <span data-testid="infowindow-ends-soon" style={{ position: "absolute", top: 10, right: 44, background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 800, padding: "4px 9px", borderRadius: 999, boxShadow: "0 4px 14px rgba(8,20,13,0.35)" }}>⏰ {remainingLabel(getRemaining(active.expiresAt), t)}</span>
                        )}
                        <p style={{ position: "absolute", left: 12, right: 12, bottom: 9, margin: 0, color: "#fff", fontSize: 16, fontWeight: 800, lineHeight: 1.2, textShadow: "0 1px 10px rgba(0,0,0,0.55)" }}>{active.title}</p>
                      </div>
                    ) : (
                      <div style={{ position: "relative", padding: "14px 14px 0" }}>
                        {active.discount > 0 && (
                          <span style={{ display: "inline-block", background: "linear-gradient(180deg,#22c55e,#0e7a3c)", color: "#fff", fontSize: 13, fontWeight: 800, padding: "4px 10px", borderRadius: 999, marginBottom: 7 }}>{t("common.percentOff", { n: active.discount })}</span>
                        )}
                        {endsSoon(active) && (
                          <span data-testid="infowindow-ends-soon" style={{ marginLeft: 6, background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 800, padding: "4px 9px", borderRadius: 999 }}>⏰ {remainingLabel(getRemaining(active.expiresAt), t)}</span>
                        )}
                        <p style={{ margin: 0, fontWeight: 800, fontSize: 16, lineHeight: 1.22, color: "#0b1f14" }}>{active.title}</p>
                      </div>
                    )}

                    <div style={{ padding: "11px 14px 14px" }}>
                      {spotLabel && (
                        <Link to={`/spot/${activeGroup[0].lat.toFixed(5)},${activeGroup[0].lng.toFixed(5)}`} data-testid="spot-venue-label"
                          style={{ margin: "0 0 7px", display: "flex", alignItems: "center", gap: 5, background: "#f3faf5", border: "1px solid #d8efe0", borderRadius: 8, padding: "6px 8px", color: "#0e6b3e", fontSize: 11, fontWeight: 800, textDecoration: "none" }}>
                          <Building2 size={12} /> {spotLabel}
                          <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 800, opacity: 0.75 }}>{t("spot.seeAll")} →</span>
                        </Link>
                      )}
                      <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, color: "#0e7a3c", fontSize: 12.5, fontWeight: 800 }}>
                        <span style={{ width: 5, height: 5, borderRadius: 999, background: "#0e7a3c", flexShrink: 0 }} />
                        {active.business}
                        {pinOpenState(active) === "open" && (
                          <span data-testid="infowindow-open-now" style={{ background: "#e7f6ec", color: "#0e7a3c", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.3 }}>{t("map.openBadge")}</span>
                        )}
                        {pinOpenState(active) === "closed" && (
                          <span data-testid="infowindow-closed-now" style={{ background: "#f1f5f9", color: "#64748b", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.3 }}>{t("map.closedBadge")}</span>
                        )}
                      </p>
                      <p style={{ margin: "3px 0 0", color: "#6b7280", fontSize: 11.5, lineHeight: 1.35 }}>{active.branchAddress}{active.branchCount > 1 ? ` · ${t("map.branchOf", { n: active.branchCount })}` : ""}</p>

                      {active.price != null && (
                        <div style={{ marginTop: 10, padding: "9px 11px", background: "#f3faf5", border: "1px solid #d8efe0", borderRadius: 13 }}>
                          <p style={{ margin: 0, display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, color: "#0e7a3c", fontSize: 20, letterSpacing: -0.4 }}>{symbolFor(active.currency)}{Number(active.price).toFixed(2)}</span>
                            {active.was != null && <span style={{ color: "#9ca3af", textDecoration: "line-through", fontSize: 12.5, fontWeight: 600 }}>{symbolFor(active.currency)}{Number(active.was).toFixed(2)}</span>}
                          </p>
                          {approx(active.price, active.currency) && (
                            <p style={{ margin: "2px 0 0", color: "#6b7280", fontSize: 10.5, fontWeight: 600 }}>{t("map.approx", { amount: approx(active.price, active.currency) })}</p>
                          )}
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 7, marginTop: 11 }}>
                        <Link to={`/deal/${active.dealId}`} data-testid="infowindow-view-deal"
                          style={{ flex: "1 1 0", textAlign: "center", background: "linear-gradient(180deg,#22c55e,#0e7a3c)", color: "#fff", fontSize: 13.5, fontWeight: 800, padding: "11px 10px", borderRadius: 12, textDecoration: "none", boxShadow: "0 6px 16px -6px rgba(14,122,60,0.75)" }}>{t("map.viewDeal")}</Link>
                        <a href={directionsUrl(active)} target="_blank" rel="noopener noreferrer" onClick={() => countDirections(active.dealId)} data-testid="infowindow-directions" title={t("map.directions")}
                          style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, background: "#0b1f14", color: "#fff", fontSize: 13, fontWeight: 800, padding: "11px 13px", borderRadius: 12, textDecoration: "none" }}>➜ {t("map.directions")}</a>
                      </div>

                      {activeGroup.length > 1 && (
                        <div style={{ marginTop: 11, borderTop: "1px solid #e8ecea", paddingTop: 9 }} data-testid="spot-deal-list">
                          <p style={{ margin: "0 0 6px", fontSize: 10.5, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>
                            {t("map.dealsAtSpot", { n: activeGroup.length })}
                            {activeBusinesses.length > 1 ? ` · ${t("map.businessesHere", { n: activeBusinesses.length })}` : ""}
                          </p>
                          <div className="lp-scroll" style={{ maxHeight: 128, overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
                            {activeBusinesses.map(([biz, list]) => (
                              <div key={biz} style={{ marginBottom: 6 }} data-testid={`spot-business-${biz}`}>
                                {activeBusinesses.length > 1 && (
                                  <p style={{ margin: "0 0 3px", fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.3 }}>{biz}</p>
                                )}
                                {list.map((p) => (
                                  <button key={`${p.dealId}-${p.branchIndex}`} onClick={() => setActive(p)} data-testid={`spot-deal-${p.dealId}`}
                                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", cursor: "pointer", padding: "8px 9px", borderRadius: 10, fontSize: 11.5, lineHeight: 1.35, color: "#0b1f14", background: p.dealId === active.dealId ? "#e7f6ec" : "transparent", marginBottom: 2 }}>
                                    <span style={{ flexShrink: 0, minWidth: 34, textAlign: "center", fontWeight: 800, color: "#fff", background: endsSoon(p) ? "#dc2626" : "#0e7a3c", fontSize: 11, padding: "3px 5px", borderRadius: 8 }}>{p.discount}%</span>
                                    <span style={{ minWidth: 0, fontWeight: 600 }}>
                                      {p.title}
                                      {endsSoon(p) && <span style={{ color: "#dc2626", fontWeight: 800 }}> · {t("map.endsSoon")}</span>}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </InfoWindowF>
              )}
            </GoogleMap>
          )}
          {GKEY && isLoaded && heatOn && (
            <div className="absolute bottom-3 left-3 z-10 bg-card/95 backdrop-blur border border-border shadow-lg rounded-xl px-3 py-2.5" data-testid="map-heat-legend">
              <p className="text-[11px] font-bold text-foreground flex items-center gap-1.5"><Flame size={12} className="text-[#b45309]" /> {t("map.heatLegend")}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                {[...HEAT_BANDS].reverse().map((b) => (
                  <span key={b.min} className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                    <span className="h-3 w-5 rounded-[4px]" style={{ background: b.color, opacity: Math.min(1, b.opacity + 0.35) }} />
                    {b.min === 0 ? "<20%" : `${b.min}%+`}
                  </span>
                ))}
              </div>
            </div>
          )}
          {GKEY && isLoaded && (
            <button onClick={toggleWide} data-testid="map-expand-toggle"
              title={mapWide ? t("map.showList") : t("map.expandMap")} aria-label={mapWide ? t("map.showList") : t("map.expandMap")}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-card/95 backdrop-blur border border-border shadow-sm text-xs font-semibold text-foreground px-3 py-2 rounded-xl hover:border-[#16a34a] hover:text-[#16a34a] transition-colors">
              {mapWide ? <><Minimize2 size={14} /> {t("map.showList")}</> : <><Maximize2 size={14} /> {t("map.expandMap")}</>}
            </button>
          )}
        </div>

        {!mapWide && (
        <div className="flex flex-col max-h-[520px] min-h-[320px]" data-testid="map-deal-list">
          <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-border">
            <p className="text-sm font-bold text-foreground flex items-center gap-1.5" data-testid="map-list-count">
              <LayoutList size={14} className="text-[#16a34a]" />
              {inViewOnly && bounds ? t(gps ? "map.nearbyCount" : "map.inViewCount", { n: dealList.length, m: totalDeals }) : t(gps ? "map.dealCountNearby" : "map.dealCount", { n: dealList.length })}
            </p>
            <div className="flex items-center gap-1">
              {dealList.length > 0 && (
                <button onClick={shareNear} data-testid="share-near-btn" title={t("map.shareNear")} aria-label={t("map.shareNear")}
                  className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-[#16a34a] hover:border-[#16a34a] transition-colors"><Share2 size={14} /></button>
              )}
              <button onClick={() => setInViewOnly((v) => !v)} data-testid="map-list-inview-toggle"
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${inViewOnly ? "bg-[#0f2a1a] text-white border-[#0f2a1a]" : "bg-card border-border text-muted-foreground hover:border-[#16a34a]"}`}>
                {inViewOnly ? t("map.inView") : t("map.all")}
              </button>
              <button onClick={resetView} data-testid="map-reset-view-btn" title={t("map.fitAll")} aria-label={t("map.fitAll")}
                className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-[#16a34a] hover:border-[#16a34a] transition-colors"><Crosshair size={14} /></button>
            </div>
          </div>
          <div className="space-y-3 overflow-y-auto lp-scroll flex-1 pt-1 pr-1" data-testid="map-deal-scroll">
          {dealList.length === 0 && (
            <div className="space-y-3" data-testid="map-list-empty">
              <p className="text-sm text-muted-foreground">
                {totalDeals > 0 ? <>{t("map.emptyInView")} <button onClick={resetView} className="font-semibold text-[#16a34a] hover:underline" data-testid="map-list-zoom-out">{t("map.zoomOut", { n: totalDeals })}</button>.</>
                  : soonOnly || openOnly || catFilter !== "All" ? t("map.emptyFilter")
                  : gps ? t("map.emptyGps") : t("map.emptyCountry", { country })}
              </p>
              {totalDeals === 0 && <InviteBusiness city={place || ""} country={country || ""} variant="button" />}
              {totalDeals === 0 && <NoDealsHere place={place || ""} country={country || ""} lat={gps?.lat} lng={gps?.lng} />}
            </div>
          )}
          {dealList.map((d) => (
            <div key={d.dealId} onClick={() => focusPin(gps ? nearestBranch(d) : d.branches[0])} data-testid={`map-list-card-${d.dealId}`} className={`cursor-pointer rounded-xl p-4 border transition-all ${active?.dealId === d.dealId ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]" : "border-border bg-card hover:border-[#16a34a]/50"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-foreground truncate">{d.title}</h3>
                  {isBoosted(d) && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#b8860b] bg-[#fdf6e3] dark:bg-[#3a2f10] px-2 py-0.5 rounded-full mt-0.5" data-testid={`map-list-boosted-${d.dealId}`}>
                      <Zap size={10} /> {t("map.boostedPin")}
                    </span>
                  )}
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 min-w-0">
                    <span className="truncate min-w-0">{d.business}</span>
                    {pinOpenState(d) === "open" && <span className="shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-[#0e7a3c] bg-[#e7f6ec] dark:bg-[#16341f] px-1.5 py-0.5 rounded-full" data-testid={`map-list-open-${d.dealId}`}>{t("map.openBadge")}</span>}
                    {pinOpenState(d) === "closed" && <span className="shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full" data-testid={`map-list-closed-${d.dealId}`}>{t("map.closedBadge")}</span>}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                    <MapPin size={12} className="text-[#16a34a]" />
                    {d.branchCount > 1 ? <><Building2 size={12} /> {d.branchCount} locations</> : d.branchAddress}
                    {d._d != null ? ` · ${formatDistance(d._d, unit)}` : ""}
                  </p>
                  {endsSoon(d) && <p className="inline-flex items-center gap-1 text-xs font-bold text-[#dc2626] mt-1" data-testid={`map-list-ends-soon-${d.dealId}`}><Clock size={12} /> {remainingLabel(getRemaining(d.expiresAt), t)}</p>}
                </div>
                <span className="shrink-0 text-xs font-bold bg-[#16a34a] text-white px-2.5 py-1 rounded-full">{t("common.percentOff", { n: d.discount })}</span>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <Link to={`/deal/${d.dealId}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a] hover:underline"><Tag size={14} /> {t("map.viewDeal")} →</Link>
                <a href={directionsUrl(gps ? nearestBranch(d) : d.branches[0])} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); countDirections(d.dealId); }} data-testid={`map-list-directions-${d.dealId}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563eb] hover:underline"><Navigation size={14} /> {t("map.directions")}</a>
              </div>
            </div>
          ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default LyteMap;
